import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleCorrectionRequestsService } from 'src/app/services/api/schedule-correction-requests/schedule-correction-requests.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  ScheduleCorrectionRequestItem,
  ScheduleCorrectionRequestListResponse,
  ScheduleCorrectionRequestListSummary,
  ScheduleCorrectionRequestModel,
  ScheduleCorrectionRequestStatus,
} from 'src/app/services/models/schedule/schedule-correction-requests.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

type CorrectionReviewTab = 'pending' | 'approved' | 'rejected' | 'all';

interface CorrectionReviewRow {
  id: number;
  sequence?: number | null;
  teacherName: string;
  teacherEmail: string;
  academicTerm: string;
  itemCount: number;
  requestedAt: string;
  status: ScheduleCorrectionRequestStatus;
  statusLabel: string;
  raw: ScheduleCorrectionRequestModel;
}

interface CorrectionReviewDataSource {
  data: CorrectionReviewRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-schedule-correction-requests-review',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './schedule-correction-requests-review.component.html',
  styleUrl: './schedule-correction-requests-review.component.scss',
})
export class ScheduleCorrectionRequestsReviewComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;

  academicTermOptions: string[] = [];
  yearOptions: string[] = [];

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly correctionRequestsService = inject(ScheduleCorrectionRequestsService);
  private readonly toast = inject(ToastService);

  readonly filterForm = this.fb.group({
    academicTerm: [this.getDefaultAcademicTermName()],
    year: [this.getDefaultBuddhistYear()],
    keywords: [''],
  });
  readonly rejectForm = this.fb.group({
    comment: ['', Validators.required],
  });

  columns: any[] = [];
  datasource = signal<CorrectionReviewDataSource>(this.createDataSource([]));
  tableLoading = signal(true);
  actionLoading = signal(false);
  activeTab = signal<CorrectionReviewTab>('all');
  summary = signal<ScheduleCorrectionRequestListSummary>({
    pending_vice_dean_review: 0,
    vice_dean_approved: 0,
    vice_dean_rejected: 0,
  });
  selectedRequest = signal<ScheduleCorrectionRequestModel | null>(null);
  detailOpen = signal(false);
  approveOpen = signal(false);
  rejectOpen = signal(false);
  pageSizeOptions = [10, 20, 50];

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly reviewedStatusByRequestId = new Map<number, ScheduleCorrectionRequestStatus>();
  private readonly subscriptions = new Subscription();
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    teacherName: 'teacher_name',
    academicTerm: 'academic_term',
    requestedAt: 'requested_at',
    status: 'status',
  };
  ngOnInit(): void {
    this.academicTermOptions = [String(this.filterForm.get('academicTerm')?.value ?? '')].filter(Boolean);
    this.yearOptions = [String(this.filterForm.get('year')?.value ?? '')].filter(Boolean);
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onTabChange(tab: CorrectionReviewTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadRequests(1);
  }

  onSearch(): void {
    this.loadRequests(1);
  }

  onView(row: CorrectionReviewRow): void {
    this.selectedRequest.set(row.raw);
    this.detailOpen.set(true);
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.detailOpen.set(false);
    this.selectedRequest.set(null);
  }

  onOpenApprove(row?: CorrectionReviewRow): void {
    const request = row?.raw ?? this.selectedRequest();
    if (!this.canReview(request)) {
      return;
    }

    this.selectedRequest.set(request);
    this.approveOpen.set(true);
  }

  onCloseApprove(): void {
    if (this.actionLoading()) {
      return;
    }

    this.approveOpen.set(false);
  }

  onConfirmApprove(): void {
    const request = this.selectedRequest();
    if (this.actionLoading() || request == null || !this.canReview(request)) {
      return;
    }

    this.actionLoading.set(true);
    this.correctionRequestsService.approveCorrectionRequest(
      request.id,
      null,
    ).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: (updatedRequest) => {
        if (updatedRequest.status !== 'vice_dean_approved') {
          this.selectedRequest.set(updatedRequest);
          this.toast.toastError('ระบบยังไม่เปลี่ยนสถานะคำขอเป็นรับรองแล้ว กรุณาลองใหม่อีกครั้ง');
          this.loadRequests(this.currentPage);
          return;
        }

        this.rememberReviewedStatus(updatedRequest);
        this.applyReviewedRequest(updatedRequest);
        this.approveOpen.set(false);
        this.detailOpen.set(false);
        this.selectedRequest.set(null);
        this.toast.toastSuccess('รับรองคำขอแก้ไขกรณีพิเศษเรียบร้อยแล้ว');
        this.loadRequests(this.currentPage);
      },
      error: (err) => this.toast.toastError(this.extractErrorMessage(err)),
    });
  }

  onOpenReject(row?: CorrectionReviewRow): void {
    const request = row?.raw ?? this.selectedRequest();
    if (!this.canReview(request)) {
      return;
    }

    this.selectedRequest.set(request);
    this.rejectForm.reset({ comment: '' });
    this.rejectOpen.set(true);
  }

  onCloseReject(): void {
    if (this.actionLoading()) {
      return;
    }

    this.rejectOpen.set(false);
  }

  onConfirmReject(): void {
    const request = this.selectedRequest();
    if (this.actionLoading() || request == null || !this.canReview(request)) {
      return;
    }

    const comment = String(this.rejectForm.getRawValue().comment ?? '').trim();
    if (this.rejectForm.invalid || comment.length === 0) {
      this.rejectForm.controls.comment.setValue(comment);
      this.rejectForm.markAllAsTouched();
      return;
    }

    this.actionLoading.set(true);
    this.correctionRequestsService.rejectCorrectionRequest(
      request.id,
      comment,
    ).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: (updatedRequest) => {
        if (updatedRequest.status !== 'vice_dean_rejected') {
          this.selectedRequest.set(updatedRequest);
          this.toast.toastError('ระบบยังไม่เปลี่ยนสถานะคำขอเป็นปฏิเสธ กรุณาลองใหม่อีกครั้ง');
          this.loadRequests(this.currentPage);
          return;
        }

        this.rememberReviewedStatus(updatedRequest);
        this.applyReviewedRequest(updatedRequest);
        this.rejectOpen.set(false);
        this.detailOpen.set(false);
        this.selectedRequest.set(null);
        this.toast.toastSuccess('ปฏิเสธคำขอแก้ไขกรณีพิเศษเรียบร้อยแล้ว');
        this.loadRequests(this.currentPage);
      },
      error: (err) => this.toast.toastError(this.extractErrorMessage(err)),
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadRequests(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadRequests(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadRequests(1);
  }

  canReview(request: ScheduleCorrectionRequestModel | null): boolean {
    return request?.status === 'pending_vice_dean_review';
  }

  statusBadgeClass(status?: ScheduleCorrectionRequestStatus | string | null): string {
    switch (this.statusDisplayGroup(status)) {
      case 'approved':
        return 'schedule-correction-status-approved';
      case 'rejected':
        return 'schedule-correction-status-rejected';
      case 'pending':
      default:
        return 'schedule-correction-status-pending';
    }
  }

  statusDisplayLabel(status?: ScheduleCorrectionRequestStatus | string | null, fallback?: string | null): string {
    switch (this.statusDisplayGroup(status, fallback)) {
      case 'pending':
        return 'รอดำเนินการ';
      case 'approved':
        return 'รับรองแล้ว';
      case 'rejected':
        return 'ปฏิเสธ';
      case 'other':
      default:
        return fallback == null || fallback === '' ? '-' : fallback;
    }
  }

  private statusDisplayGroup(
    status?: ScheduleCorrectionRequestStatus | string | null,
    fallback?: string | null,
  ): 'pending' | 'approved' | 'rejected' | 'other' {
    const normalizedStatus = String(status ?? '').trim();
    const normalizedFallback = String(fallback ?? '').trim();
    const displayValue = normalizedStatus === '' ? normalizedFallback : normalizedStatus;

    switch (displayValue) {
      case 'pending':
      case 'pending_vice_dean_review':
      case 'รอพิจารณา':
      case 'รอดำเนินการ':
        return 'pending';
      case 'approved':
      case 'vice_dean_approved':
      case 'completed':
      case 'finished':
      case 'done':
      case 'อนุมัติ':
      case 'อนุมัติแล้ว':
      case 'เสร็จสิ้น':
      case 'รับรองแล้ว':
        return 'approved';
      case 'rejected':
      case 'vice_dean_rejected':
      case 'ไม่อนุมัติ':
      case 'ปฏิเสธ':
        return 'rejected';
      default:
        return normalizedFallback === '' ? 'other' : this.statusDisplayGroup(normalizedFallback);
    }
  }

  itemSubjectCode(item: ScheduleCorrectionRequestItem): string {
    return this.stringValue(item.approvable?.subject_code ?? item.metadata?.['subject_code']);
  }

  itemSubjectName(item: ScheduleCorrectionRequestItem): string {
    return this.stringValue(item.approvable?.subject_name ?? item.metadata?.['subject_name']);
  }

  itemTeachingType(item: ScheduleCorrectionRequestItem): string {
    const metadataName = item.metadata?.['teaching_type_name'];
    if (typeof metadataName === 'string' && metadataName.trim() !== '') {
      return metadataName;
    }

    if (item.teaching_type === 'SPC') {
      return 'รายวิชาโครงงาน / วิจัย';
    }

    return item.teaching_type ?? '-';
  }

  workloadSplitLabel(item: ScheduleCorrectionRequestItem): string {
    return item.is_workload_split === true ? 'แบ่งภาระงาน' : 'ไม่แบ่งภาระงาน';
  }

  formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  academicTermLabel(request: ScheduleCorrectionRequestModel): string {
    const term = request.schedule_submission?.academic_term;
    if (term == null) {
      return '-';
    }

    return `${term.name}/${this.toBuddhistYear(term.year)}`;
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 180,
        minWidth: 160,
      },
      {
        prop: 'academicTerm',
        name: 'ภาคการศึกษา',
        sortable: true,
        width: 130,
        minWidth: 120,
      },
      {
        prop: 'itemCount',
        name: 'จำนวนรายวิชาที่ขอแก้',
        sortable: false,
        width: 150,
        minWidth: 140,
      },
      {
        prop: 'requestedAt',
        name: 'วันที่ส่งคำขอ',
        sortable: true,
        width: 160,
        minWidth: 150,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: true,
        cellTemplate: this.statusTemplate,
        width: 130,
        minWidth: 120,
        cellClass: 'text-center schedule-correction-status-cell',
        headerClass: 'text-center schedule-correction-status-header',
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 96,
        minWidth: 88,
        cellClass: 'text-center schedule-correction-action-cell',
        headerClass: 'text-center schedule-correction-action-header',
      },
    ];
  }

  private listenFilters(): void {
    this.subscriptions.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(250),
          distinctUntilChanged(),
        )
        .subscribe(() => this.loadRequests(1)),
    );
  }

  private loadRequests(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);
    const selectedTerm = this.selectedAcademicTerm();
    this.correctionRequestsService.getCorrectionRequests({
      status: this.statusForActiveTab(),
      academic_term_id: selectedTerm?.id ?? null,
      keywords: String(this.filterForm.getRawValue().keywords ?? '').trim() || null,
      page,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => this.applyListResponse(response),
      error: (err) => {
        this.toast.toastError(this.extractErrorMessage(err));
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private applyListResponse(response: ScheduleCorrectionRequestListResponse): void {
    const activeStatus = this.statusForActiveTab();
    const normalizedRequests = response.data
      .map((request) => this.withLocallyReviewedStatus(request))
      .filter((request) => this.matchesActiveStatus(request.status, activeStatus));
    const removedStaleRows = response.data.length - normalizedRequests.length;

    this.datasource.set(this.createDataSource(
      normalizedRequests.map((request) => this.mapRequestToRow(request)),
      response.page,
      response.limit,
      Math.max(response.total - removedStaleRows, 0),
      response.total_pages,
    ));
    this.summary.set(this.withLocallyReviewedSummary(response.summary, response.data));
  }

  private defaultSummary(): ScheduleCorrectionRequestListSummary {
    return {
      pending_vice_dean_review: 0,
      vice_dean_approved: 0,
      vice_dean_rejected: 0,
    };
  }

  private withLocallyReviewedSummary(
    summary: ScheduleCorrectionRequestListSummary | undefined,
    requests: ScheduleCorrectionRequestModel[],
  ): ScheduleCorrectionRequestListSummary {
    const nextSummary = { ...(summary ?? this.defaultSummary()) };

    requests.forEach((request) => {
      const reviewedStatus = this.reviewedStatusByRequestId.get(request.id);
      if (reviewedStatus == null || reviewedStatus === request.status) {
        return;
      }

      this.incrementSummary(nextSummary, request.status, -1);
      this.incrementSummary(nextSummary, reviewedStatus, 1);
    });

    return nextSummary;
  }

  private incrementSummary(
    summary: ScheduleCorrectionRequestListSummary,
    status: ScheduleCorrectionRequestStatus,
    delta: number,
  ): void {
    const key = this.summaryKeyForStatus(status);
    if (key == null) {
      return;
    }

    summary[key] = Math.max(summary[key] + delta, 0);
  }

  private summaryKeyForStatus(
    status: ScheduleCorrectionRequestStatus,
  ): keyof ScheduleCorrectionRequestListSummary | null {
    switch (status) {
      case 'pending_vice_dean_review':
        return 'pending_vice_dean_review';
      case 'approved':
      case 'vice_dean_approved':
      case 'completed':
      case 'finished':
      case 'done':
        return 'vice_dean_approved';
      case 'rejected':
      case 'vice_dean_rejected':
        return 'vice_dean_rejected';
      case 'pending':
      case 'editing':
      case 'resubmitted_for_approval':
      case 'cancelled':
        return null;
    }
  }

  private rememberReviewedStatus(request: ScheduleCorrectionRequestModel): void {
    if (request.status === 'vice_dean_approved' || request.status === 'vice_dean_rejected') {
      this.reviewedStatusByRequestId.set(request.id, request.status);
    }
  }

  private withLocallyReviewedStatus(
    request: ScheduleCorrectionRequestModel,
  ): ScheduleCorrectionRequestModel {
    const reviewedStatus = this.reviewedStatusByRequestId.get(request.id);

    if (reviewedStatus == null || request.status === reviewedStatus) {
      return request;
    }

    return {
      ...request,
      status: reviewedStatus,
      status_label: null,
    };
  }

  private applyReviewedRequest(updatedRequest: ScheduleCorrectionRequestModel): void {
    const activeStatus = this.statusForActiveTab();
    const currentRows = this.datasource().data;
    const nextRows = currentRows
      .map((row) => (row.id === updatedRequest.id ? this.mapRequestToRow(updatedRequest) : row))
      .filter((row) => this.matchesActiveStatus(row.status, activeStatus));
    const removedRows = currentRows.length - nextRows.length;

    if (removedRows > 0 || nextRows.some((row) => row.id === updatedRequest.id)) {
      this.datasource.set(this.createDataSource(
        nextRows,
        this.datasource().page,
        this.datasource().limit,
        Math.max(this.datasource().total - removedRows, 0),
        this.datasource().total_page,
      ));
    }
  }

  private statusForActiveTab(): string | null {
    switch (this.activeTab()) {
      case 'pending':
        return 'pending_vice_dean_review';
      case 'approved':
        return 'vice_dean_approved';
      case 'rejected':
        return 'vice_dean_rejected';
      case 'all':
      default:
        return null;
    }
  }

  private matchesActiveStatus(
    status: ScheduleCorrectionRequestStatus | string | null | undefined,
    activeStatus: string | null,
  ): boolean {
    if (activeStatus == null) {
      return true;
    }

    return this.statusDisplayGroup(status) === this.statusDisplayGroup(activeStatus);
  }

  private mapRequestToRow(request: ScheduleCorrectionRequestModel): CorrectionReviewRow {
    const term = request.schedule_submission?.academic_term;

    return {
      id: request.id,
      sequence: request.sequence ?? null,
      teacherName: this.teacherDisplayName(request),
      teacherEmail: request.schedule_submission?.teacher?.email ?? request.requested_by?.email ?? '-',
      academicTerm: term != null ? `${term.name}/${this.toBuddhistYear(term.year)}` : '-',
      itemCount: request.items?.length ?? 0,
      requestedAt: this.formatDateTime(request.requested_at),
      status: request.status,
      statusLabel: this.statusDisplayLabel(request.status, request.status_label),
      raw: request,
    };
  }

  private createDataSource(
    rows: CorrectionReviewRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
  ): CorrectionReviewDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private toBuddhistYear(year: number): number {
    return year < 2400 ? year + 543 : year;
  }

  private loadFilterOptions(): void {
    this.subscriptions.add(
      forkJoin({
        academicTerms: this.studyPeriodService.getAcademicTermYears().pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            return of([] as AcademicTermYearModel[]);
          }),
        ),
      }).subscribe(({ academicTerms }) => {
        this.academicTerms = academicTerms;
        const academicTermOptions = this.buildAcademicTermOptions(academicTerms);
        const yearOptions = this.buildAcademicYearOptions(academicTerms);

        this.academicTermOptions = academicTermOptions.length > 0
          ? academicTermOptions
          : this.academicTermOptions;
        this.yearOptions = yearOptions.length > 0
          ? yearOptions
          : this.yearOptions;
        this.ensureAcademicTermFilterValues();
        this.loadRequests(1);
      }),
    );
  }

  private selectedAcademicTerm(): AcademicTermYearModel | undefined {
    const value = this.filterForm.getRawValue();
    return this.findAcademicTerm(
      String(value.academicTerm ?? ''),
      String(value.year ?? ''),
    );
  }

  teacherDisplayName(request: ScheduleCorrectionRequestModel): string {
    const teacher = request.schedule_submission?.teacher ?? request.requested_by;
    if (teacher == null) {
      return '-';
    }

    const displayName = String(teacher.teacher_display_name ?? '').trim();
    if (displayName !== '') {
      return displayName;
    }

    const academicRank = String(teacher.academic_rank ?? '').trim();
    const fullName = String(teacher.full_name).trim();

    return [academicRank, fullName].filter(Boolean).join(' ') || '-';
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.filterForm.get('academicTerm')?.value ?? '');
    const currentYear = String(this.filterForm.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.filterForm.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private stringValue(value: unknown): string {
    return typeof value === 'string' && value.trim() !== '' ? value : '-';
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object') {
        const errors = (error as { errors?: unknown; error?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return errors.join('\n');
        }

        const message = (error as { error?: unknown }).error;
        if (typeof message === 'string' && message.trim() !== '') {
          return message;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง';
  }
}
